import { useRef, useState } from "react";
import { UploadCloud, FileText, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

const ACCEPT = ["application/pdf", "image/jpeg", "image/png"];

export default function UploadDropzone({ onUpload, busy, busyLabel }) {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [groupPages, setGroupPages] = useState(false);
  const [rejected, setRejected] = useState([]);

  const add = (list) => {
    const ok = [], bad = [];
    Array.from(list).forEach((f) => {
      if (!ACCEPT.includes(f.type)) bad.push(`${f.name}: unsupported type`);
      else if (f.size > 15 * 1024 * 1024) bad.push(`${f.name}: larger than 15MB`);
      else ok.push(f);
    });
    setRejected(bad);
    setFiles((prev) => [...prev, ...ok].slice(0, 30));
  };

  return (
    <div className="space-y-4" data-testid="upload-section">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="upload-dropzone"
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 sm:p-12 text-center transition-colors ${drag ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-white hover:border-slate-400"}`}
      >
        <UploadCloud className="mx-auto h-10 w-10 text-emerald-600 mb-3" />
        <p className="font-semibold text-slate-900">Drop invoices here or tap to browse</p>
        <p className="text-sm text-slate-500 mt-1">PDF, JPG or PNG · up to 15MB each · batch up to 30 files</p>
        <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="upload-file-input" />
      </div>

      {rejected.length > 0 && <ul className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="upload-rejected">{rejected.map((r) => <li key={r}>{r}</li>)}</ul>}

      {files.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3 fade-up" data-testid="upload-file-list">
          <ul className="divide-y divide-slate-100 max-h-60 overflow-auto">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-3 py-2 text-sm">
                <FileText className="h-4 w-4 text-slate-400" /><span className="flex-1 truncate">{f.name}</span>
                <span className="text-xs text-slate-400 font-mono">{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600" data-testid={`remove-file-${i}`}><X className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
          {files.length > 1 && (
            <label className="flex items-center justify-between gap-3 text-sm bg-slate-50 rounded-md px-3 py-2">
              <span><span className="font-medium">These files are pages of ONE invoice</span><span className="block text-xs text-slate-500">Off = each file is a separate invoice</span></span>
              <Switch checked={groupPages} onCheckedChange={setGroupPages} data-testid="group-pages-switch" />
            </label>
          )}
          <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-base" disabled={busy} onClick={() => onUpload(files, groupPages).then(() => setFiles([]))} data-testid="upload-submit-button">
            {busy ? (busyLabel || "Uploading…") : `Upload ${files.length} file${files.length > 1 ? "s" : ""} for AI reading`}
          </Button>
        </div>
      )}
    </div>
  );
}
